import axios from 'axios';

const resolveApiBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  try {
    const parsedUrl = new URL(configuredUrl);
    const isLocalHost = parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';
    const runtimeHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const runtimeIsRemote = runtimeHost && runtimeHost !== 'localhost' && runtimeHost !== '127.0.0.1';

    if (isLocalHost && runtimeIsRemote) {
      parsedUrl.hostname = runtimeHost;
      return parsedUrl.toString().replace(/\/$/, '');
    }

    return configuredUrl.replace(/\/$/, '');
  } catch (e) {
    return configuredUrl.replace(/\/$/, '');
  }
};

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Improved global response interceptor for session expiration/revocation
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      (error.response.data.error === 'Session expired' || error.response.data.error === 'Session revoked')
    ) {
      // Only auto-logout for session expiration/revocation
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
};

// User API
export const userAPI = {
  getAllUsers: () => api.get('/users'),
  getAdminAllUsers: () => api.get('/users/admin/all-users'),
  getUserById: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  toggleMonthlyReport: (id, has_monthly_report) => api.put(`/users/${id}/toggle-monthly-report`, { has_monthly_report }),
  resetUserPassword: (id) => api.put(`/users/${id}/reset-password`),
};

// Subscription API
export const subscriptionAPI = {
  createSubscription: (data) => api.post('/subscriptions', data),
  getAllSubscriptions: () => api.get('/subscriptions'),
  getUserSubscriptions: (userId) => api.get(`/subscriptions/user/${userId}`),
  getSubscriptionById: (id) => api.get(`/subscriptions/${id}`),
  updateSubscription: (id, data) => api.put(`/subscriptions/${id}`, data),
  updateLifecycle: (id, action) => api.put(`/subscriptions/${id}/lifecycle`, { action }),
  cancelSubscription: (id) => api.delete(`/subscriptions/${id}`),
};

export const planAPI = {
  createPlan: (data) => api.post('/plans', data),
  getAllPlans: () => api.get('/plans'),
  updatePlan: (id, data) => api.put(`/plans/${id}`, data),
};

export const updatePaymentInfo = (userId, data) => {
  return api.put(`/users/${userId}/payment-info`, data);
};

export const adminAPI = {
  getDashboardKpis: () => api.get('/admin/dashboard-kpis'),
  getUserInsights: () => api.get('/admin/user-insights'),
  getSessions: (params = {}) => api.get('/admin/sessions', { params }),
  revokeSession: (id) => api.put(`/admin/sessions/${id}/revoke`),
  getReportedIssues: () => api.get('/issues/all'),
  deleteReportedIssue: (id) => api.delete(`/issues/${id}`),
};

export default api;
